import { formatPoiPriceFromCents } from '@/point-of-interest-price'

interface PoiMapGarage {
    id: number
    name: string
    address: string
    minimum_stay_label: string
    lat: number
    lng: number
    distance_km: number
    reserve_url: string
    image_url: string
}

interface PoiMapPayload {
    poi: {
        lat: number
        lng: number
        name: string
    }
    garages: PoiMapGarage[]
    config: {
        google_maps_key: string
        locale: string
        currency: string
        price_endpoint: string
        labels: {
            reserve: string
            distance: string
            amount_per_hour: string
            amount_for_24_hours: string
            amount_per_hour_wrapped: string
            loading_price: string
            loading_price_error: string
        }
    }
}

interface GarageBatchPriceRow {
    garage_id: number
    amount_cents_hourly_czk?: number
    amount_cents_hourly_eur?: number
}

interface GarageBatchPriceResponse {
    prices?: GarageBatchPriceRow[]
}

interface GaragePriceDetails {
    total: string | null
    hourly: string | null
}

declare global {
    interface Window {
        google?: typeof google
        __poiGoogleMapsPromise?: Promise<typeof google>
        __poiGoogleMapsCallback?: () => void
    }
}

const GOOGLE_MAPS_SCRIPT_ID = 'poi-google-maps-api-script'

function loadGoogleMapsApi(apiKey: string): Promise<typeof google> {
    if (window.google?.maps) {
        return Promise.resolve(window.google)
    }

    if (window.__poiGoogleMapsPromise) {
        return window.__poiGoogleMapsPromise
    }

    window.__poiGoogleMapsPromise = new Promise((resolve, reject) => {
        const existingScript = document.getElementById(
            GOOGLE_MAPS_SCRIPT_ID
        ) as HTMLScriptElement | null
        if (existingScript) {
            existingScript.addEventListener('load', () => {
                if (window.google?.maps) {
                    resolve(window.google)
                } else {
                    reject(new Error('Google Maps API loaded without maps namespace'))
                }
            })
            existingScript.addEventListener('error', () =>
                reject(new Error('Failed to load Google Maps API'))
            )
            return
        }

        window.__poiGoogleMapsCallback = () => {
            if (!window.google?.maps) {
                reject(new Error('Google Maps API callback fired without maps namespace'))
                return
            }

            resolve(window.google)
            Reflect.deleteProperty(window, '__poiGoogleMapsCallback')
        }

        const script = document.createElement('script')
        script.id = GOOGLE_MAPS_SCRIPT_ID
        script.async = true
        script.defer = true
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&callback=__poiGoogleMapsCallback`
        script.addEventListener('error', () =>
            reject(new Error('Failed to load Google Maps API script'))
        )

        document.head.appendChild(script)
    })

    return window.__poiGoogleMapsPromise
}

function formatDistance(template: string, distanceKm: number): string {
    const rounded = Number.isInteger(distanceKm)
        ? distanceKm.toString()
        : distanceKm.toFixed(1).replace(/\.0$/, '')
    return template.replace('%{distance}', rounded)
}

function escapeHtml(value: string): string {
    return value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;')
}

function getDefaultPriceWindow(): {
    beginsAtDate: string
    beginsAtTime: string
    endsAtDate: string
    endsAtTime: string
} {
    const start = new Date()
    start.setMinutes(0, 0, 0)
    start.setHours(start.getHours() + 1)

    const end = new Date(start)
    end.setHours(end.getHours() + 24)

    const toDateString = (date: Date): string => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    const toTimeString = (date: Date): string => {
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${hours}:${minutes}`
    }

    return {
        beginsAtDate: toDateString(start),
        beginsAtTime: toTimeString(start),
        endsAtDate: toDateString(end),
        endsAtTime: toTimeString(end),
    }
}

function escapeSvgText(value: string): string {
    return value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;')
}

function normalizeMarkerPriceText(rawPriceText: string): string {
    if (rawPriceText.length <= 18) {
        return rawPriceText
    }

    return `${rawPriceText.slice(0, 17)}…`
}

function buildGarageMarkerIcon(priceText: string, selected: boolean): google.maps.Icon {
    const text = normalizeMarkerPriceText(priceText)
    const textLength = text.length
    const bubbleWidth = Math.max(56, Math.min(144, Math.round(textLength * 7 + 16)))
    const bubbleHeight = 24
    const pointerHeight = 8
    const totalHeight = bubbleHeight + pointerHeight
    const centerX = bubbleWidth / 2
    const pointerHalfWidth = 6
    const bubbleColor = selected ? '#020101' : '#ffcc00'
    const textColor = selected ? '#ffcc00' : '#020101'

    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${bubbleWidth}" height="${totalHeight}" viewBox="0 0 ${bubbleWidth} ${totalHeight}">
  <rect x="0" y="0" width="${bubbleWidth}" height="${bubbleHeight}" rx="5" ry="5" fill="${bubbleColor}"/>
  <path d="M ${centerX - pointerHalfWidth} ${bubbleHeight - 1} L ${centerX + pointerHalfWidth} ${bubbleHeight - 1} L ${centerX} ${totalHeight - 1} Z" fill="${bubbleColor}"/>
  <text x="${centerX}" y="${bubbleHeight / 2 + 0.5}" fill="${textColor}" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="13" font-weight="600">${escapeSvgText(text)}</text>
</svg>`

    return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new google.maps.Size(bubbleWidth, totalHeight),
        anchor: new google.maps.Point(centerX, totalHeight),
    }
}

function buildPoiMarkerIcon(): google.maps.Icon {
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
  <path d="M14 1C7.2 1 1.8 6.4 1.8 13.2c0 9.6 12.2 25.6 12.2 25.6s12.2-16 12.2-25.6C26.2 6.4 20.8 1 14 1z" fill="#ea4335" stroke="#c5221f" stroke-width="1.25"/>
  <ellipse cx="14" cy="14" rx="6.2" ry="6.2" fill="#c5221f"/>
</svg>`

    return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new google.maps.Size(28, 40),
        anchor: new google.maps.Point(14, 40),
    }
}

function parsePayload(container: HTMLElement): PoiMapPayload | null {
    const mapDataId = container.getAttribute('data-map-id')
    if (!mapDataId) {
        return null
    }

    const scriptTag = document.getElementById(mapDataId)
    if (!(scriptTag instanceof HTMLScriptElement)) {
        return null
    }

    try {
        return JSON.parse(scriptTag.textContent || '{}') as PoiMapPayload
    } catch (_error) {
        return null
    }
}

async function fetchGaragePrice(
    payload: PoiMapPayload,
    garageId: number
): Promise<GaragePriceDetails> {
    const range = getDefaultPriceWindow()
    const params = new URLSearchParams()
    params.set('price[begins_at_date]', range.beginsAtDate)
    params.set('price[begins_at_time]', range.beginsAtTime)
    params.set('price[ends_at_date]', range.endsAtDate)
    params.set('price[ends_at_time]', range.endsAtTime)
    params.set('price[garage_id]', garageId.toString())

    const response = await fetch(`${payload.config.price_endpoint}?${params.toString()}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
        },
    })

    if (!response.ok) {
        return { total: null, hourly: null }
    }

    const data = (await response.json()) as {
        amount_cents_czk?: number
        amount_cents_eur?: number
        amount_cents_hourly_czk?: number
        amount_cents_hourly_eur?: number
    }

    const currentCurrency = payload.config.currency.toLowerCase() === 'eur' ? 'eur' : 'czk'
    const totalCents = currentCurrency === 'eur' ? data.amount_cents_eur : data.amount_cents_czk
    const hourlyCents =
        currentCurrency === 'eur' ? data.amount_cents_hourly_eur : data.amount_cents_hourly_czk

    if (totalCents == null && hourlyCents == null) {
        return { total: null, hourly: null }
    }

    const total =
        totalCents != null
            ? formatPoiPriceFromCents(totalCents, currentCurrency, payload.config.locale)
            : null
    const hourly =
        hourlyCents != null
            ? formatPoiPriceFromCents(hourlyCents, currentCurrency, payload.config.locale)
            : null

    return { total, hourly }
}

async function fetchGaragePricesBatch(
    payload: PoiMapPayload,
    garageIds: number[]
): Promise<Map<number, string>> {
    if (garageIds.length === 0) {
        return new Map()
    }

    const range = getDefaultPriceWindow()
    const params = new URLSearchParams()
    params.set('begins_at_date', range.beginsAtDate)
    params.set('begins_at_time', range.beginsAtTime)
    params.set('ends_at_date', range.endsAtDate)
    params.set('ends_at_time', range.endsAtTime)
    for (const garageId of garageIds) {
        params.append('garage_ids[]', garageId.toString())
    }

    const response = await fetch(`/api/garages/prices_in_batch?${params.toString()}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
        },
    })

    if (!response.ok) {
        return new Map()
    }

    const data = (await response.json()) as GarageBatchPriceResponse
    const formattedPrices = new Map<number, string>()
    const currentCurrency = payload.config.currency.toLowerCase() === 'eur' ? 'eur' : 'czk'

    for (const row of data.prices || []) {
        const hourlyCents =
            currentCurrency === 'eur' ? row.amount_cents_hourly_eur : row.amount_cents_hourly_czk
        if (hourlyCents == null) {
            continue
        }

        const hourlyAmount = formatPoiPriceFromCents(
            hourlyCents,
            currentCurrency,
            payload.config.locale
        )
        formattedPrices.set(
            row.garage_id,
            payload.config.labels.amount_per_hour.replace('%{amount}', hourlyAmount)
        )
    }

    return formattedPrices
}

function renderGarageCard(
    cardNode: HTMLElement,
    payload: PoiMapPayload,
    garage: PoiMapGarage,
    totalPriceContent: string,
    hourlyPriceContent: string
): void {
    const imageHtml = garage.image_url
        ? `<img class="point-of-interest-page__map-card-image" src="${escapeHtml(garage.image_url)}" alt="" loading="lazy" />`
        : ''

    cardNode.innerHTML = `
    <button type="button" class="point-of-interest-page__map-card-close" data-close-card aria-label="Close">×</button>
    ${imageHtml}
    <div class="point-of-interest-page__map-card-content">
      <h3 class="point-of-interest-page__map-card-title">${escapeHtml(garage.name)}</h3>
      <p class="point-of-interest-page__map-card-address">${escapeHtml(garage.address)}</p>
      <p class="point-of-interest-page__map-card-distance">${escapeHtml(formatDistance(payload.config.labels.distance, garage.distance_km))}</p>
      <p class="point-of-interest-page__map-card-minimum">${escapeHtml(garage.minimum_stay_label)}</p>
      <div class="point-of-interest-page__map-card-pricing">
        <p class="point-of-interest-page__map-card-price-secondary" data-price-hourly>${escapeHtml(hourlyPriceContent)}</p>
        <div class="point-of-interest-page__map-card-price-row">
          <p class="point-of-interest-page__map-card-price" data-price-total>${escapeHtml(totalPriceContent)}</p>
          <a class="button small secondary point-of-interest-page__map-card-action" href="${escapeHtml(garage.reserve_url)}">${escapeHtml(payload.config.labels.reserve)}</a>
        </div>
      </div>
    </div>
  `
    cardNode.hidden = false
}

function buildCardPricePlaceholders(payload: PoiMapPayload): {
    total: string
    hourly: string
} {
    const amountPlaceholder = '---'

    return {
        total: payload.config.labels.amount_for_24_hours.replace('%{amount}', amountPlaceholder),
        hourly: payload.config.labels.amount_per_hour.replace('%{amount}', amountPlaceholder),
    }
}

async function mountPoiMap(container: HTMLElement): Promise<void> {
    const payload = parsePayload(container)
    if (!payload || !payload.config.google_maps_key) {
        return
    }

    const canvas = container.querySelector('[data-map-canvas]')
    const card = container.querySelector('[data-map-card]')

    if (!(canvas instanceof HTMLElement) || !(card instanceof HTMLElement)) {
        return
    }

    const maps = await loadGoogleMapsApi(payload.config.google_maps_key)

    const map = new maps.maps.Map(canvas, {
        center: { lat: payload.poi.lat, lng: payload.poi.lng },
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        gestureHandling: 'greedy',
    })

    const bounds = new maps.maps.LatLngBounds()
    const priceCache = new Map<number, GaragePriceDetails>()
    const markerByGarageId = new Map<number, google.maps.Marker>()
    const markerPriceByGarageId = new Map<number, string>()
    const cardPricePlaceholders = buildCardPricePlaceholders(payload)

    bounds.extend({ lat: payload.poi.lat, lng: payload.poi.lng })

    new maps.maps.Marker({
        map,
        position: { lat: payload.poi.lat, lng: payload.poi.lng },
        icon: buildPoiMarkerIcon(),
        title: payload.poi.name,
        zIndex: 2,
    })

    let selectedGarageId: number | null = null

    const updateGarageMarkerAppearance = (garageId: number): void => {
        const marker = markerByGarageId.get(garageId)
        if (!marker) {
            return
        }

        const priceText = markerPriceByGarageId.get(garageId) || '...'
        const selected = selectedGarageId === garageId
        marker.setIcon(buildGarageMarkerIcon(priceText, selected))
        marker.setZIndex(selected ? 20 : 10)
    }

    const resetSelectedMarker = (): void => {
        if (selectedGarageId === null) {
            return
        }

        const previouslySelectedGarageId = selectedGarageId
        selectedGarageId = null
        updateGarageMarkerAppearance(previouslySelectedGarageId)
    }

    const closeCard = (): void => {
        resetSelectedMarker()
        card.hidden = true
        card.innerHTML = ''
    }

    for (const garage of payload.garages) {
        bounds.extend({ lat: garage.lat, lng: garage.lng })

        const marker = new maps.maps.Marker({
            map,
            position: { lat: garage.lat, lng: garage.lng },
            icon: buildGarageMarkerIcon('...', false),
            title: garage.name,
            zIndex: 10,
        })

        markerByGarageId.set(garage.id, marker)

        marker.addListener('click', async () => {
            resetSelectedMarker()
            selectedGarageId = garage.id
            updateGarageMarkerAppearance(garage.id)

            renderGarageCard(
                card,
                payload,
                garage,
                cardPricePlaceholders.total,
                cardPricePlaceholders.hourly
            )
            map.panTo({ lat: garage.lat, lng: garage.lng })

            const closeButton = card.querySelector('[data-close-card]')
            if (closeButton instanceof HTMLButtonElement) {
                closeButton.addEventListener('click', closeCard, { once: true })
            }

            if (!priceCache.has(garage.id)) {
                try {
                    const price = await fetchGaragePrice(payload, garage.id)
                    priceCache.set(garage.id, price)
                } catch (_error) {
                    priceCache.set(garage.id, { total: null, hourly: null })
                }
            }

            if (selectedGarageId !== garage.id) {
                return
            }

            const cachedPrice = priceCache.get(garage.id) || { total: null, hourly: null }

            const totalPriceNode = card.querySelector('[data-price-total]')
            if (totalPriceNode instanceof HTMLElement) {
                totalPriceNode.textContent = cachedPrice.total
                    ? payload.config.labels.amount_for_24_hours.replace(
                          '%{amount}',
                          cachedPrice.total
                      )
                    : ''
            }

            const hourlyPriceNode = card.querySelector('[data-price-hourly]')
            if (hourlyPriceNode instanceof HTMLElement) {
                hourlyPriceNode.textContent = cachedPrice.hourly
                    ? payload.config.labels.amount_per_hour.replace('%{amount}', cachedPrice.hourly)
                    : payload.config.labels.loading_price_error
            }
        })
    }

    const batchPrices = await fetchGaragePricesBatch(
        payload,
        payload.garages.map(garage => garage.id)
    )
    for (const [garageId, garagePrice] of batchPrices.entries()) {
        markerPriceByGarageId.set(garageId, garagePrice)
        updateGarageMarkerAppearance(garageId)
    }

    if (payload.garages.length > 0) {
        map.fitBounds(bounds, 64)
        maps.maps.event.addListenerOnce(
            map,
            'bounds_changed',
            function adjustZoom(this: google.maps.Map) {
                const zoom = this.getZoom()
                if (zoom && zoom > 14) {
                    this.setZoom(14)
                }
            }
        )
    }

    map.addListener('click', closeCard)
}

document.addEventListener('DOMContentLoaded', () => {
    const container = document.getElementById('point-of-interest-map')
    if (!container) {
        return
    }

    void mountPoiMap(container)
})

export type {}
